import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthTokenService } from './auth-token.service';
import { SessionService } from './session.service';
import { UtilityService } from './utility.service';
import { BrowserLocation, BrowserStorage, BrowserWindow, CognitoTokenResponse } from '../app.models';
import { Cognito, Storage } from '../app.constants';

@Injectable({ providedIn: 'root' })
export class UserStateService {
  private readonly authTokenService = inject(AuthTokenService);
  private readonly browserStorage = inject(BrowserStorage);
  private readonly browserLocation = inject(BrowserLocation);
  private readonly browserWindow = inject(BrowserWindow);
  private readonly sessionService = inject(SessionService);
  private readonly utilityService = inject(UtilityService);
  private readonly router = inject(Router);

  readonly userUuid = signal<string | undefined>(undefined);
  readonly isSignedIn = signal(false);

  async loadUser(): Promise<void> {
    try {
      const userInfo = await this.authTokenService.getUserInfo();
      this.userUuid.set(userInfo.userUuid);
      this.isSignedIn.set(true);
    } catch {
      // No valid token — user remains signed out
    }
  }

  signIn(): void {
    const currentUrl = new URL(this.browserLocation.toString());
    const redirectUri = `${currentUrl.origin}/post-sign-in`;

    const cognitoUrl = new URL(`${Cognito.url}/oauth2/authorize`);
    cognitoUrl.searchParams.set('client_id', Cognito.clientId);
    cognitoUrl.searchParams.set('redirect_uri', redirectUri);
    cognitoUrl.searchParams.set('response_type', Cognito.responseType);
    cognitoUrl.searchParams.set('identity_provider', Cognito.identityProvider);
    cognitoUrl.searchParams.set('scope', 'openid email profile');
    this.browserLocation.replace(cognitoUrl.toString());
  }

  async handleSignInSuccess(authCode: string): Promise<void> {
    const currentUrl = new URL(this.browserLocation.toString());
    const tokenUrl = new URL(`${Cognito.url}/oauth2/token`);
    const searchParams = new URLSearchParams();
    searchParams.set('grant_type', 'authorization_code');
    searchParams.set('client_id', Cognito.clientId);
    searchParams.set('scope', 'openid email profile');
    searchParams.set('redirect_uri', `${currentUrl.origin}/post-sign-in`);
    searchParams.set('code', authCode || '');

    const response = await this.browserWindow.fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: searchParams.toString()
    });
    const data: CognitoTokenResponse = await response.json();

    this.browserStorage.setItem(Storage.IdToken, data.id_token);
    this.browserStorage.setItem(Storage.AccessToken, data.access_token);
    this.browserStorage.setItem(Storage.RefreshToken, data.refresh_token);
    this.sessionService.recordSessionEvent(data.id_token, 'sign-in').then();

    const userUuid = this.utilityService.getTokenProperty(data.id_token, 'sub') || '';
    this.userUuid.set(userUuid);
    this.isSignedIn.set(true);

    this.router.navigate(['/template-list']).then();
  }

  handleSignInFailure(): void {
    this.router.navigate(['/home']);
  }

  signOut(): void {
    this.browserStorage.removeItem(Storage.IdToken);
    this.browserStorage.removeItem(Storage.AccessToken);
    this.browserStorage.removeItem(Storage.RefreshToken);

    const currentUrl = new URL(this.browserLocation.toString());
    const logoutUri = `${currentUrl.origin}/post-sign-out`;

    const url = new URL(`${Cognito.url}/logout`);
    url.searchParams.set('client_id', Cognito.clientId);
    url.searchParams.set('logout_uri', logoutUri);
    this.browserLocation.replace(url.toString());
  }

  handleSignOutSuccess(): void {
    this.userUuid.set(undefined);
    this.isSignedIn.set(false);
    this.router.navigate(['/home']);
  }
}
